import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Cloud, Download, RefreshCw, CheckCircle2, AlertTriangle, ExternalLink, Calendar, FileSpreadsheet } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Modules exposed as downloads — matches sheets_backup.export_config_for on the backend.
const MODULES = [
  { slug: 'sales',          label: 'Sales Orders',       hint: 'Every sales order + voucher link' },
  { slug: 'vouchers',       label: 'Vouchers / Invoices', hint: 'GST + Non-GST invoices' },
  { slug: 'purchases',      label: 'Purchase Orders',    hint: 'All vendor POs' },
  { slug: 'bills',          label: 'Purchase Bills',     hint: 'Booked vendor bills' },
  { slug: 'inventory-rm',   label: 'Raw Materials',      hint: 'Stock master' },
  { slug: 'inventory-fg',   label: 'Finished Goods',     hint: 'FG master + stock' },
  { slug: 'lots',           label: 'Inventory Lots',     hint: 'FIFO lot tracker' },
  { slug: 'batches',        label: 'Production Batches', hint: 'All production runs' },
  { slug: 'recipes',        label: 'Recipes',            hint: 'Active recipe master' },
  { slug: 'clients',        label: 'Clients',            hint: 'Customer master' },
  { slug: 'vendors',        label: 'Vendors',            hint: 'Supplier master' },
  { slug: 'cashflow',       label: 'Cashflow',           hint: 'All money movements' },
  { slug: 'tasks',          label: 'Tasks',              hint: 'Task manager items' },
  { slug: 'dispatches',     label: 'Dispatches',         hint: 'Logistics records' },
  { slug: 'employees',      label: 'Employees',          hint: 'Workforce master' },
  { slug: 'portal-orders',  label: 'Portal Orders',      hint: 'Customer-portal orders' },
];

export default function BackupExport() {
  const [info, setInfo] = useState(null);
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);

  const loadStatus = async () => {
    try {
      const [i, s] = await Promise.all([
        axios.get(`${API}/backup/info`, { withCredentials: true }),
        axios.get(`${API}/backup/status`, { withCredentials: true }),
      ]);
      setInfo(i.data); setStatus(s.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load backup status');
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const runBackup = async () => {
    if (!window.confirm('This mirrors every tracked collection to Google Sheets (takes ~45 seconds). Run now?')) return;
    setRunning(true);
    const t = toast.loading('Backing up to Google Sheets…');
    try {
      const res = await axios.post(`${API}/backup/run`, {}, { withCredentials: true, timeout: 180000 });
      const d = res.data;
      toast.success(`Backup complete · ${d.tab_count} tabs · ${d.duration_sec}s${d.error_count ? ` · ${d.error_count} error(s)` : ''}`, { id: t });
      loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Backup failed', { id: t });
    } finally { setRunning(false); }
  };

  const downloadXlsx = (slug) => {
    window.open(`${API}/exports/${slug}.xlsx`, '_blank');
  };
  const downloadCsv = (slug) => {
    window.open(`${API}/exports/${slug}.csv`, '_blank');
  };

  const last = status?.last_run;
  const lastWhen = last?.logged_at ? new Date(last.logged_at).toLocaleString('en-IN') : '—';

  return (
    <div className="flex flex-col space-y-6" data-testid="backup-export-page">
      <div>
        <h1 className="font-heading text-2xl font-semibold text-zinc-50">Backup &amp; Export</h1>
        <p className="text-zinc-500 text-sm mt-1">Google Sheets backup · Excel/CSV downloads per module</p>
      </div>

      {/* Google Sheets backup card */}
      <div className="bg-gradient-to-br from-green-500/10 to-cyan-500/10 border border-green-500/30 rounded-xl p-5" data-testid="backup-card">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-green-500/20 border border-green-500/40 flex items-center justify-center">
              <Cloud size={18} className="text-green-300" />
            </div>
            <div>
              <h2 className="font-heading font-semibold text-zinc-50 text-lg">Google Sheets Backup</h2>
              <p className="text-zinc-400 text-xs mt-0.5">
                Automatic daily snapshot · <span className="text-green-300 font-medium">{info?.schedule || 'Daily at 02:00 IST'}</span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {info?.sheet_url && (
              <a href={info.sheet_url} target="_blank" rel="noreferrer"
                data-testid="open-sheet-btn"
                className="text-xs px-3 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 flex items-center gap-1.5 border border-zinc-700 transition-colors">
                Open Sheet <ExternalLink size={12} />
              </a>
            )}
            <button onClick={runBackup} disabled={running}
              data-testid="run-backup-btn"
              className="text-xs px-3 py-2 rounded-lg bg-green-500 hover:bg-green-400 disabled:bg-zinc-700 text-black font-semibold flex items-center gap-1.5 transition-all">
              <RefreshCw size={12} className={running ? 'animate-spin' : ''} /> {running ? 'Running…' : 'Backup Now'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="bg-zinc-950/60 border border-zinc-800 rounded-xl p-3">
            <div className="text-zinc-500 text-[10px] uppercase tracking-wider">Last backup</div>
            <div className="text-zinc-100 text-sm font-mono mt-1 flex items-center gap-1.5">
              <Calendar size={11} className="text-zinc-500" /> {lastWhen}
            </div>
            {last?.triggered_by && <div className="text-zinc-600 text-[10px] mt-0.5">by {last.triggered_by}</div>}
          </div>
          <div className="bg-zinc-950/60 border border-zinc-800 rounded-xl p-3">
            <div className="text-zinc-500 text-[10px] uppercase tracking-wider">Tabs mirrored</div>
            <div className="text-zinc-100 text-sm font-mono mt-1 flex items-center gap-1.5">
              {last?.error_count === 0
                ? <CheckCircle2 size={11} className="text-green-400" />
                : <AlertTriangle size={11} className="text-amber-400" />}
              {last?.tab_count ?? (info?.covered?.length ?? '—')}
              {typeof last?.duration_sec === 'number' && <span className="text-zinc-600 ml-1">· {last.duration_sec}s</span>}
            </div>
            {typeof last?.error_count === 'number' && last.error_count > 0 && (
              <div className="text-amber-300 text-[10px] mt-0.5">{last.error_count} tab error(s)</div>
            )}
          </div>
          <div className="bg-zinc-950/60 border border-zinc-800 rounded-xl p-3">
            <div className="text-zinc-500 text-[10px] uppercase tracking-wider">Service account</div>
            <div className="text-zinc-300 text-[11px] font-mono mt-1 break-all">{info?.service_account || '—'}</div>
          </div>
        </div>

        {info?.covered && info.covered.length > 0 && (
          <div className="mt-4">
            <div className="text-zinc-500 text-[10px] uppercase tracking-wider mb-2">Tracked tabs ({info.covered.length})</div>
            <div className="flex flex-wrap gap-1.5">
              {info.covered.map(c => (
                <span key={c.tab} className="text-[10px] px-2 py-0.5 rounded-md bg-zinc-900 text-zinc-300 border border-zinc-800 font-mono">
                  {c.tab}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Per-module Excel / CSV */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5" data-testid="export-card">
        <div className="flex items-center gap-2 mb-4">
          <FileSpreadsheet size={16} className="text-amber-400" />
          <h2 className="font-heading font-semibold text-zinc-50 text-sm">Download data by module</h2>
        </div>
        <p className="text-zinc-500 text-xs mb-4">One-click Excel (.xlsx) or CSV per module. Files download via your browser — no cloud step.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {MODULES.map(m => (
            <div key={m.slug} className="bg-zinc-950/60 border border-zinc-800 rounded-xl p-4 hover:border-zinc-700 transition-colors" data-testid={`export-row-${m.slug}`}>
              <div className="text-zinc-100 text-sm font-semibold">{m.label}</div>
              <div className="text-zinc-500 text-[11px] mt-0.5">{m.hint}</div>
              <div className="flex gap-1.5 mt-3">
                <button onClick={() => downloadXlsx(m.slug)}
                  data-testid={`export-xlsx-${m.slug}`}
                  className="flex-1 text-[11px] px-2.5 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-semibold flex items-center gap-1 justify-center transition-all">
                  <Download size={11} /> Excel
                </button>
                <button onClick={() => downloadCsv(m.slug)}
                  data-testid={`export-csv-${m.slug}`}
                  className="flex-1 text-[11px] px-2.5 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700 flex items-center gap-1 justify-center transition-colors">
                  <Download size={11} /> CSV
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
